import type {Logro} from '@cnc/contracts';
import {Link} from 'react-router';
import {useLogros, useMarcarLogrosVistos} from '../api/logros';
import {tr} from '../i18n/idioma';
import {fecha} from '../utilidades/formato';

const COLORES_CONFETI = ['#16775f', '#d8ad5e', '#9a722c', '#3b6fb6', '#b3261e', '#e0c48a'];
// Posiciones y tiempos fijos (no aleatorios): el confeti cae igual siempre.
const CONFETI = Array.from({length: 28}, (_, i) => ({
  izquierda: (i * 37) % 100,
  retraso: ((i * 13) % 10) / 10,
  duracion: 2.2 + ((i * 7) % 10) / 10,
  color: COLORES_CONFETI[i % COLORES_CONFETI.length]!,
  giro: (i * 47) % 360,
}));

// Aviso de logros nuevos (exportacion 100, Big Bag 9.000...): aparece en la
// esquina con un poco de confeti y se cierra con "¡Genial!".
export function AvisoDeLogros() {
  const logros = useLogros();
  const marcar = useMarcarLogrosVistos();
  const nuevos = logros.data?.nuevos ?? [];
  if (!nuevos.length) return null;
  const grande = nuevos.some(l => l.grande);
  const visibles = nuevos.slice(0, 3);
  const cerrar = () => void marcar.mutateAsync(Math.max(...nuevos.map(l => l.id)));

  return (
    <>
      <style>{`@keyframes cnc-confeti {0% {transform: translateY(-10vh) rotate(0deg); opacity: 1} 100% {transform: translateY(105vh) rotate(720deg); opacity: 0.2}}`}</style>
      <div aria-hidden className="pointer-events-none fixed inset-0 z-40 overflow-hidden motion-reduce:hidden">
        {CONFETI.slice(0, grande ? 28 : 14).map((c, i) => (
          <span
            key={i}
            className="absolute top-0 block h-3 w-2 rounded-sm"
            style={{
              left: `${c.izquierda}%`,
              background: c.color,
              transform: `rotate(${c.giro}deg)`,
              animation: `cnc-confeti ${c.duracion}s ease-in ${c.retraso}s 2 both`,
            }}
          />
        ))}
      </div>
      <section
        role="dialog"
        aria-label={tr('Logros nuevos', 'New achievements')}
        className="fixed right-6 bottom-6 z-50 w-96 max-w-[calc(100vw-2rem)] rounded-xl border-2 border-dorado bg-white p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <div aria-hidden className="text-4xl leading-none">
            🏆
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold tracking-wide text-dorado uppercase">
              {nuevos.length === 1
                ? tr('¡Nuevo logro!', 'New achievement!')
                : tr('¡Nuevos logros!', 'New achievements!')}
            </div>
            <ul className="mt-1 space-y-2">
              {visibles.map(l => (
                <LineaDeLogro key={l.id} logro={l} />
              ))}
            </ul>
            {nuevos.length > visibles.length && (
              <p className="mt-1 text-xs text-apagado">
                {tr(`Y ${nuevos.length - visibles.length} más.`, `And ${nuevos.length - visibles.length} more.`)}
              </p>
            )}
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={cerrar}
                disabled={marcar.isPending}
                className="rounded-md bg-verde px-4 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
              >
                {tr('¡Genial!', 'Great!')}
              </button>
              <Link to="/logros" onClick={cerrar} className="text-sm text-verde hover:underline">
                {tr('Ver todos los logros', 'See all achievements')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function LineaDeLogro({logro: l}: {logro: Logro}) {
  return (
    <li>
      <div className={`font-semibold text-verde-oscuro ${l.grande ? 'text-lg' : ''}`}>{l.titulo}</div>
      <div className="text-xs text-apagado">
        {l.detalle}
        {l.fecha
          ? l.tipo === 'exportacion'
            ? tr(` Salida: ${fecha(l.fecha)}.`, ` Departure: ${fecha(l.fecha)}.`)
            : tr(` Empacado el ${fecha(l.fecha)}.`, ` Packed on ${fecha(l.fecha)}.`)
          : ''}
      </div>
    </li>
  );
}
